/* @refresh reset */
import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  pdf,
} from "@react-pdf/renderer";
import { IBudgetFormData, BudgetRecipientEnum } from "../components/budgetsForm/createAndEdit/types";
import { BudgetStatusEnum, getBudgetStatusLabel } from "../enums/budgetStatus";
import {
  parseDiscountPercent,
  parseQuantityToNumber,
} from "../components/budgetsForm/createAndEdit/utils/budgetCalculations";
import { formatRecipientLabel } from "../components/budgetsForm/utils";

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 10,
    fontFamily: "Helvetica",
  },
  title: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 12,
  },
  header: {
    marginBottom: 12,
  },
  budgetInfo: {
    fontSize: 11,
    marginBottom: 12,
  },
  section: {
    marginBottom: 16,
    paddingBottom: 8,
    borderBottom: "1px solid #e5e7eb",
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 8,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  twoColumns: {
    flexDirection: "row",
    gap: 12,
    marginBottom: 12,
  },
  column: {
    flex: 1,
  },
  bold: {
    fontWeight: "bold",
  },
  table: {
    marginTop: 8,
    marginBottom: 8,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f3f4f6",
    padding: 8,
    borderBottom: "1px solid #d1d5db",
  },
  tableRow: {
    flexDirection: "row",
    padding: 6,
    borderBottom: "1px solid #e5e7eb",
    minHeight: 20,
  },
  tableCell: {
    fontSize: 9,
  },
  colRef: { width: "7%" },
  colItem: { width: "13%" },
  colDesc: { width: "24%" },
  colQty: { width: "9%", textAlign: "right" },
  colValue: { width: "10%", textAlign: "right" },
  colCobertura: { width: "12%" },
  colDiscount: { width: "9%", textAlign: "right" },
  colSubtotal: { width: "16%", textAlign: "right" },
  summary: {
    marginTop: 12,
    padding: 10,
    backgroundColor: "#f9fafb",
    border: "1px solid #d1d5db",
    borderRadius: 4,
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 4,
    gap: 12,
  },
  statusBadge: {
    backgroundColor: "#9ca3af",
    color: "white",
    padding: "4px 8px",
    borderRadius: 4,
    fontSize: 9,
    fontWeight: "bold",
  },
  infoBar: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 16,
    paddingBottom: 10,
    borderBottom: "1px solid #e5e7eb",
  },
  infoBarItem: {
    fontSize: 8,
    color: "#6b7280",
    marginBottom: 2,
  },
  infoBarValue: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#000000",
  },
  statusBadgePending: {
    backgroundColor: "#9ca3af",
    color: "white",
    padding: "4px 8px",
    borderRadius: 9999,
    fontSize: 9,
    fontWeight: "bold",
  },
  statusBadgeApproved: {
    backgroundColor: "#9ca3af",
    color: "white",
    padding: "4px 8px",
    borderRadius: 9999,
    fontSize: 9,
    fontWeight: "bold",
  },
  statusBadgeRejected: {
    backgroundColor: "#9ca3af",
    color: "white",
    padding: "4px 8px",
    borderRadius: 9999,
    fontSize: 9,
    fontWeight: "bold",
  },
  vehicleCard: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  warrantyBadge: {
    backgroundColor: "#9ca3af",
    color: "white",
    padding: "4px 8px",
    borderRadius: 9999,
    fontSize: 8,
    fontWeight: "bold",
    alignSelf: "flex-start",
  },
});

const BudgetPdfDocument = ({ data }: { data: IBudgetFormData }) => {
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatDate = (iso?: string): string => {
    if (!iso) return "";
    let isoString = iso.trim();
    if (!isoString.endsWith("Z") && !isoString.match(/[+-]\d{2}:\d{2}$/)) {
      isoString = `${isoString}Z`;
    }
    const d = new Date(isoString);
    const date = d.toLocaleDateString("pt-BR", {
      timeZone: "America/Sao_Paulo",
    });
    const time = d.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "America/Sao_Paulo",
    });
    return `${date} - ${time}`;
  };

  const originLabel = data.origin ? formatRecipientLabel(data.origin) : "";
  const recipientLabel = data.recipient
    ? formatRecipientLabel(data.recipient)
    : "";

  const getStatusBadgeStyle = (status?: BudgetStatusEnum | string) => {
    if (!status) return styles.statusBadgePending;
    const statusStr = String(status);
    if (statusStr === BudgetStatusEnum.APPROVED || statusStr === "Aprovado") {
      return styles.statusBadgeApproved;
    }
    if (statusStr === BudgetStatusEnum.REJECTED || statusStr === "Recusado") {
      return styles.statusBadgeRejected;
    }
    return styles.statusBadgePending;
  };

  const getStatusLabel = (status?: BudgetStatusEnum | string): string => {
    if (!status) return "Pendente";
    return getBudgetStatusLabel(status);
  };

  const formatDateOnly = (iso?: string): string => {
    if (!iso) return "";
    let isoString = iso.trim();
    if (!isoString.endsWith("Z") && !isoString.match(/[+-]\d{2}:\d{2}$/)) {
      isoString = `${isoString}Z`;
    }
    const d = new Date(isoString);
    return d.toLocaleDateString("pt-BR", {
      timeZone: "America/Sao_Paulo",
    });
  };

  const consultantName = 
    data.recipient === BudgetRecipientEnum.CONSULTANT || data.recipient === "CONSULTANT"
      ? data.consultant || ""
      : "";
  
  const managerName = 
    data.recipient === BudgetRecipientEnum.MANAGER || data.recipient === "MANAGER"
      ? data.userProfile?.name || ""
      : data.userProfile?.name && data.profile === BudgetRecipientEnum.MANAGER
      ? data.userProfile.name
      : "";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.budgetInfo}>
            <Text style={styles.bold}>
              OCOR: {data.vehicleData?.protocolNumber || ""}
            </Text>
          </Text>
          <Text style={styles.budgetInfo}>
            <Text style={styles.bold}>
              Orçamento #{data.number || ""}
            </Text>
          </Text>
        </View>

        <View style={styles.twoColumns}>
          <View style={styles.column}>
            <Text style={styles.sectionTitle}>Veículo</Text>
            <View
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 4,
                padding: 12,
              }}
            >
              <View
                style={{
                  backgroundColor: "#f9fafb",
                  borderRadius: 4,
                  padding: 12,
                }}
              >
                <View style={styles.vehicleCard}>
                  <View style={{ flex: 1 }}>
                    {data.vehicleData?.model && (
                      <Text style={[styles.bold, { marginBottom: 4, fontSize: 11 }]}>
                        {data.vehicleData.model}
                        {data.vehicleData.year && ` ${data.vehicleData.year}`}
                        {data.vehicleData.licensePlate &&
                          ` | Placa: ${data.vehicleData.licensePlate}`}
                      </Text>
                    )}
                    {data.vehicleData?.chassis && (
                      <Text style={{ marginBottom: 4, fontSize: 10 }}>
                        Chassi - {data.vehicleData.chassis}
                      </Text>
                    )}
                    {(data.vehicleData?.odometer || data.vehicleData?.hourmeter) && (
                      <Text style={{ marginBottom: 4, fontSize: 10 }}>
                        {data.vehicleData.odometer &&
                          `Odômetro: ${data.vehicleData.odometer}`}
                        {data.vehicleData.odometer &&
                          data.vehicleData.hourmeter &&
                          " | "}
                        {data.vehicleData.hourmeter &&
                          `Horímetro: ${data.vehicleData.hourmeter}`}
                      </Text>
                    )}
                    {data.vehicleData?.contractNumber && (
                      <Text style={[styles.bold, { fontSize: 10 }]}>
                        Volks total Prev: Contrato #{data.vehicleData.contractNumber}
                      </Text>
                    )}
                  </View>
                  {data.vehicleData?.inWarranty && (
                    <View style={styles.warrantyBadge}>
                      <Text>Em garantia</Text>
                    </View>
                  )}
                </View>
              </View>
            </View>
          </View>

          <View style={styles.column}>
            <Text style={styles.sectionTitle}>Cliente</Text>
            <View
              style={{
                border: "1px solid #e5e7eb",
                padding: 12,
                borderRadius: 4,
              }}
            >
              {data.clientData?.name && (
                <Text style={[styles.bold, { marginBottom: 8 }]}>
                  {data.clientData.name}
                </Text>
              )}
              <View style={styles.row}>
                <Text>{data.clientData?.phone || ""}</Text>
                <Text>{data.clientData?.email || ""}</Text>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.infoBar}>
          <View>
            <Text style={styles.infoBarItem}>Consultor da concessionária</Text>
            <Text style={styles.infoBarValue}>{consultantName || "-"}</Text>
          </View>
          <View>
            <Text style={styles.infoBarItem}>Destinatário</Text>
            <Text style={styles.infoBarValue}>{recipientLabel || "-"}</Text>
          </View>
          <View>
            <Text style={styles.infoBarItem}>Versão</Text>
            <Text style={styles.infoBarValue}>
              {data.versionNumber ? data.versionNumber.toFixed(2) : "1.00"}
            </Text>
          </View>
          <View>
            <Text style={styles.infoBarItem}>Data</Text>
            <Text style={styles.infoBarValue}>
              {data.counterProposalAcceptedAt
                ? formatDateOnly(data.counterProposalAcceptedAt)
                : formatDateOnly(new Date().toISOString())}
            </Text>
          </View>
          <View>
            <View style={getStatusBadgeStyle(data.status)}>
              <Text>{getStatusLabel(data.status)}</Text>
            </View>
          </View>
        </View>

        <View style={{ marginBottom: 12 }}>
          <Text style={[styles.sectionTitle, { marginBottom: 10, fontSize: 13 }]}>Valores</Text>
          {data.services.map((service, serviceIndex) => {
            const visibleItems = (service.items || []).filter((item) => {
              if (item.removed === true) return false;
              const quantity = parseQuantityToNumber(item.quantity);
              return quantity > 0;
            });

            const serviceName =
              service.serviceName || service.serviceId || "Serviço";

            return (
              <View
                key={service.id || `service-${serviceIndex}`}
                style={{
                  marginBottom: 12,
                  border: "1px solid #e5e7eb",
                  padding: 10,
                  borderRadius: 4,
                }}
              >
                <View
                  style={{
                    backgroundColor: "#f3f4f6",
                    padding: 6,
                    borderRadius: 4,
                    marginBottom: 8,
                  }}
                >
                  <Text style={[styles.bold, { fontSize: 11 }]}>
                    {serviceName}
                  </Text>
                </View>

                <View style={styles.table}>
                  <View style={styles.tableHeader}>
                    <Text
                      style={[styles.tableCell, styles.colRef, styles.bold]}
                    >
                      Ref.
                    </Text>
                    <Text
                      style={[styles.tableCell, styles.colItem, styles.bold]}
                    >
                      Item
                    </Text>
                    <Text
                      style={[styles.tableCell, styles.colDesc, styles.bold]}
                    >
                      Descrição
                    </Text>
                    <Text
                      style={[styles.tableCell, styles.colQty, styles.bold]}
                    >
                      Quantidade
                    </Text>
                    <Text
                      style={[styles.tableCell, styles.colValue, styles.bold]}
                    >
                      Valor (R$)
                    </Text>
                    <Text
                      style={[
                        styles.tableCell,
                        styles.colCobertura,
                        styles.bold,
                      ]}
                    >
                      Cobertura
                    </Text>
                    <Text
                      style={[
                        styles.tableCell,
                        styles.colDiscount,
                        styles.bold,
                      ]}
                    >
                      % De desconto
                    </Text>
                    <Text
                      style={[
                        styles.tableCell,
                        styles.colSubtotal,
                        styles.bold,
                      ]}
                    >
                      Subtotal (R$)
                    </Text>
                  </View>
                  {visibleItems.map((item, itemIndex) => {
                    const quantity = parseQuantityToNumber(item.quantity);
                    const discountPercent = parseDiscountPercent(item.discount);
                    const isCovered = item.isCovered === true;
                    const itemSubtotal = isCovered
                      ? 0
                      : quantity * item.value * (1 - discountPercent / 100);
                    const isRemoved = item.removed === true;

                    return (
                      <View
                        key={`item-${item.ref}-${itemIndex}`}
                        style={[
                          styles.tableRow,
                          isRemoved ? { backgroundColor: "#f9fafb" } : {},
                        ]}
                      >
                        <Text style={[styles.tableCell, styles.colRef]}>
                          {item.ref || ""}
                        </Text>
                        <Text style={[styles.tableCell, styles.colItem]}>
                          {item.item || ""}
                        </Text>
                        <Text style={[styles.tableCell, styles.colDesc]}>
                          {item.description || ""}
                        </Text>
                        <Text style={[styles.tableCell, styles.colQty]}>
                          {item.quantity || ""}
                        </Text>
                        <Text style={[styles.tableCell, styles.colValue]}>
                          {formatCurrency(item.value)}
                        </Text>
                        <Text style={[styles.tableCell, styles.colCobertura]}>
                          {isCovered ? "Coberto pelo plano" : "Sem cobertura"}
                        </Text>
                        <Text style={[styles.tableCell, styles.colDiscount]}>
                          {item.discount || "0%"}
                        </Text>
                        <Text style={[styles.tableCell, styles.colSubtotal]}>
                          {formatCurrency(itemSubtotal)}
                        </Text>
                      </View>
                    );
                  })}
                </View>

                {visibleItems.length > 0 && (
                  <View style={{ marginTop: 6, alignItems: "flex-end" }}>
                    <Text style={{ fontSize: 9, color: "#000000" }}>
                      Subtotal do serviço:{" "}
                      <Text style={styles.bold}>
                        {formatCurrency(
                          visibleItems.reduce((sum, item) => {
                            if (item.isCovered === true) return sum;
                            const quantity = parseQuantityToNumber(
                              item.quantity
                            );
                            const discountPercent = parseDiscountPercent(
                              item.discount
                            );
                            const itemSubtotal =
                              quantity *
                              item.value *
                              (1 - discountPercent / 100);
                            return sum + itemSubtotal;
                          }, 0)
                        )}
                      </Text>
                    </Text>
                  </View>
                )}
              </View>
            );
          })}
        </View>

        <View style={styles.summary}>
          <View style={styles.summaryRow}>
            <Text style={{ color: "#6b7280" }}>Subtotal:</Text>
            <Text style={styles.bold}>
              {formatCurrency(data.summary.subtotal)}
            </Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={{ color: "#000000" }}>Descontos:</Text>
            <Text style={styles.bold}>
              {formatCurrency(data.summary.discounts)}
            </Text>
          </View>
          {data.counterProposalAcceptedAt && (
            <View
              style={[
                styles.summaryRow,
                { textDecoration: "line-through", opacity: 0.5 },
              ]}
            >
              <Text style={[styles.bold, { fontSize: 12 }]}>
                Valor a ser pago:
              </Text>
              <Text style={[styles.bold, { fontSize: 12 }]}>
                {formatCurrency(data.summary.totalToPay)}
              </Text>
            </View>
          )}
          {data.counterProposalAcceptedAt && data.counterProposalValue ? (
            <View style={styles.summaryRow}>
              <Text style={[styles.bold, { fontSize: 12 }]}>
                Valor a ser pago:
              </Text>
              <Text style={[styles.bold, { fontSize: 12 }]}>
                {formatCurrency(data.counterProposalValue)}
              </Text>
            </View>
          ) : (
            <View style={styles.summaryRow}>
              <Text style={[styles.bold, { fontSize: 12 }]}>
                Valor a ser pago:
              </Text>
              <Text style={[styles.bold, { fontSize: 12 }]}>
                {formatCurrency(data.summary.totalToPay)}
              </Text>
            </View>
          )}
        </View>

      </Page>
    </Document>
  );
};

export const downloadBudgetPdf = async (budgetData: IBudgetFormData) => {
  try {
    const doc = <BudgetPdfDocument data={budgetData} />;
    const asPdf = pdf(doc);
    const blob = await asPdf.toBlob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Orcamento_${budgetData.number || budgetData.budgetId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("[downloadBudgetPdf] Erro ao gerar PDF:", error);
    throw error;
  }
};
